import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { services } from "@/data/content";

export default function Services() {
    return (
        <div>
            <Seo
                title="Executive Search & Talent Research Services | Vantage Crest Research"
                description="Eight research-grade services: end-to-end recruitment, talent pooling, mapping, pipelining, research & insight, executive headhunting, CV formatting and lead generation."
            />

            <section
                className="relative overflow-hidden bg-[#0C0C0C] pt-40 pb-24"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 80% 15%, rgba(18,18,18,0.8) 0%, rgba(6,6,6,0.95) 55%, #060606 100%)",
                }}
            >
                <div className="absolute inset-0 grid-lines-dark pointer-events-none" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <Eyebrow dark>Services</Eyebrow>
                    <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-[#F7F5F1] mt-6 max-w-4xl">
                        Research-grade capability,
                        <br />
                        <span className="text-[#D8BC7A]">across the full talent cycle.</span>
                    </h1>
                    <p className="mt-8 text-lg text-[#A6A69C] leading-relaxed max-w-2xl">
                        From proactive market mapping to confidential senior-level
                        headhunting — eight services, each delivered with the rigour of a
                        research house.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3">
                        {services.map((s) => (
                            <a
                                key={s.id}
                                href={`#${s.id}`}
                                className="font-mono-accent text-[10px] tracking-[0.18em] uppercase border border-white/15 text-[#A6A69C] hover:border-[#C9A24B]/60 hover:text-[#D8BC7A] px-3.5 py-2 transition-colors duration-200"
                            >
                                {s.code}
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    {services.map((s, i) => (
                        <div
                            key={s.id}
                            id={s.id}
                            data-testid={`service-detail-${s.id}`}
                            className="scroll-mt-24 grid lg:grid-cols-12 gap-8 lg:gap-14 py-16 sm:py-20 border-b border-[#E7E4DC] last:border-b-0"
                        >
                            <Reveal className="lg:col-span-5">
                                <div className="flex items-center gap-4">
                                    <span className="font-mono-accent text-sm text-[#C9A24B] tracking-widest">
                                        {s.code}
                                    </span>
                                    <span className="h-px flex-1 bg-[#E7E4DC]" />
                                    <span className="font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#77776E] border border-[#E7E4DC] px-2.5 py-1">
                                        {s.badge}
                                    </span>
                                </div>
                                <div className="mt-7 flex items-start gap-5">
                                    <span className="shrink-0 w-12 h-12 border border-[#C9A24B]/50 flex items-center justify-center">
                                        <s.icon size={22} className="text-[#C9A24B]" strokeWidth={1.5} />
                                    </span>
                                    <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#141414] leading-snug">
                                        {s.title}
                                    </h2>
                                </div>
                            </Reveal>
                            <div className="lg:col-span-7">
                                <Reveal delay={0.08}>
                                    <p className="text-base text-[#4E4E48] leading-relaxed">
                                        {s.description}
                                    </p>
                                </Reveal>
                                <Reveal delay={0.14}>
                                    <p className="mt-8 font-mono-accent text-xs tracking-[0.24em] uppercase text-[#C9A24B]">
                                        What you get
                                    </p>
                                    <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                                        {s.deliverables.map((d, j) => (
                                            <li key={j} className="flex items-start gap-2.5 text-sm text-[#4E4E48] leading-relaxed">
                                                <Check size={15} className="mt-0.5 shrink-0 text-[#C9A24B]" />
                                                {d}
                                            </li>
                                        ))}
                                    </ul>
                                </Reveal>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-[#171717] grid-lines-dark py-20">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <Reveal>
                        <Eyebrow dark>Engage Us</Eyebrow>
                        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#F6F4EF] mt-4 leading-snug max-w-xl">
                            Not sure which service fits? Brief us — we'll scope it.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact"
                            data-testid="services-cta-contact"
                            className="inline-flex items-center gap-2 bg-[#C9A24B] hover:bg-[#D8BC7A] text-[#0C0C0C] font-semibold px-7 py-3.5 text-sm transition-colors duration-200 active:scale-[0.98]"
                        >
                            Get in Touch <ArrowUpRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
